import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import CarnivalShell from './CarnivalShell';
import { submitGame } from './carnivalSlice';
import { gameByKey } from './games';
import { isDemoSession } from './session';

const pad = (n) => String(n).padStart(2, '0');
const hhmmss = (ms) => {
  const t = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(t / 3600))}:${pad(Math.floor((t % 3600) / 60))}:${pad(t % 60)}`;
};

const PLAY_KEY = 'carnival.play';
const PENDING_KEY = 'carnival.pendingScore';
const readPending = () => {
  try {
    return JSON.parse(sessionStorage.getItem(PENDING_KEY));
  } catch {
    return null;
  }
};

// One game (<game>/game.php). The canvas game is a normal page under /carnival/games/<game>/ (no iframe):
//   /carnival/play/<game>        -> checks the player may play, then opens the game page
//   /carnival/play/<game>?done=1 -> the game's bridge.js sent us back with the score; we save it and go to the hub
// Only a player's first attempt is counted — the server enforces that.
export default function CarnivalPlay() {
  const { gameKey } = useParams();
  const [params] = useSearchParams();
  const done = params.get('done') === '1';
  const game = gameByKey(gameKey);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSelector((s) => s.carnival.session);
  const handled = useRef(false);

  const toHub = () => navigate('/carnival/games', { replace: true });
  const demoBlocked = Boolean(session) && isDemoSession(session) && process.env.NODE_ENV === 'production';

  // Launch: leave the React app for the game page, passing its rules along for the in-game Rules button.
  useEffect(() => {
    if (!game || done || demoBlocked) return;
    try {
      sessionStorage.setItem(PLAY_KEY, JSON.stringify({ key: game.key, title: game.title, rules: game.rules }));
      sessionStorage.removeItem(PENDING_KEY);
    } catch {
      /* the game still opens, just without the Rules button */
    }
    window.location.replace(game.src);
  }, [game, done, demoBlocked]);

  // Result: save the score the game handed over.
  useEffect(() => {
    if (!game || !done || !session || handled.current) return;
    handled.current = true;
    const pending = readPending();
    try {
      sessionStorage.removeItem(PENDING_KEY);
    } catch {
      /* ignore */
    }
    if (!pending || pending.gameKey !== game.key) {
      toHub();
      return;
    }
    const { score, lines, startedAt } = pending;
    const time = hhmmss(Date.now() - startedAt);

    const save = async () => {
      Swal.fire({ title: 'Saving your score…', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const res = await dispatch(submitGame({ gameKey: game.key, score, time, lines }));
      if (submitGame.fulfilled.match(res)) {
        const { alreadyPlayed, score: saved } = res.payload;
        await Swal.fire({
          icon: 'success',
          title: alreadyPlayed ? 'Thank you for playing!' : 'Thank you for submission',
          text: alreadyPlayed
            ? `Points are only added for your first attempt (you scored ${saved} that time).`
            : `You scored ${saved} points.`,
          confirmButtonText: 'Back to games',
          allowOutsideClick: false,
        });
        toHub();
      } else {
        const { isConfirmed } = await Swal.fire({
          icon: 'error',
          title: 'Could not save your score',
          text: res.payload || 'Please check your connection and try again.',
          confirmButtonText: 'Try again',
          showCancelButton: true,
          cancelButtonText: 'Back to games',
          allowOutsideClick: false,
        });
        if (isConfirmed) save();
        else toHub();
      }
    };
    save();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, done, session]);

  if (!game) return <Navigate to="/carnival/games" replace />;
  // Direct links must not bypass the demo lock on the hub.
  if (demoBlocked) return <Navigate to="/carnival/games" replace />;

  // While the redirect happens (or the score is saved) show the usual page frame with the player logged in.
  return <CarnivalShell className="cc-play" bgColor={game.bg} />;
}
